import { DragAndDrop } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import olMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import Stroke from 'ol/style/Stroke';
import Style, { StyleLike } from 'ol/style/Style';
import { AppStorage } from './AppStorage';
import GPX from 'ol/format/GPX';
import GeoJSON from 'ol/format/GeoJSON';
import * as olExtent from 'ol/extent';
import { Popover } from "bootstrap"
import FeatureFormat from 'ol/format/Feature';

export interface IGpxFiles {  
    filename: string;
    colour: string;    
}

export interface IGpxFile {
    extent: olExtent.Extent;
    geoJson: string;
}

export class MapGpx {

    private dragAndDropInteraction: DragAndDrop;
    private featureLayer: VectorLayer;
    private layerSource: VectorSource;
    private appStorage: AppStorage;
    private defaultColour: string = "#646463";

    public constructor(private map: olMap) {
        this.appStorage = new AppStorage();
        this.addFeatureLayer();
        this.addGpxFileInputHandler();
        this.addDragDropInteraction();
        this.addRemoveAllHandler();
        this.loadFromStorage();
    }

    private addFeatureLayer = (): void => {
        this.layerSource = new VectorSource();
        this.featureLayer = new VectorLayer({
            source: this.layerSource,
            style: this.getFeatureStyle(this.defaultColour)
        });

        this.map.addLayer(this.featureLayer);
    }

    private getFeatureStyle = (darkStrokeColour: string): StyleLike => {
        let lightStroke = new Style({
            stroke: new Stroke({
                color: [255, 255, 255, 0.7],
                width: 4,
                lineDash: [4, 8],
                lineDashOffset: 6
            })
        });

        let darkStroke = new Style({
            stroke: new Stroke({
                //color: [0, 0, 0, 0.6],
                color: darkStrokeColour,
                width: 4,
                lineDash: [4, 8]
            })
        });

        return [lightStroke, darkStroke]
    }

    private addGpxFileInputHandler = (): void => {
        let self = this;

        //gpxFile
        $("#gpxFile").on("change", function (e) {
            let files = (<HTMLInputElement>e.target).files;
            for (let i = 0, f; f = files[i]; i++) {
                let file = f;
                self.getFeaturesFromGpxFile(f, function (features) {
                    self.loadGpx(file, features);
                });
            }
        });
    }

    private addDragDropInteraction = (): void => {
        this.dragAndDropInteraction = new DragAndDrop({
            formatConstructors: [                
                <any>GPX
                //FeatureFormat.
                //ol.format.GPX
                //ol.format.GeoJSON,
                //ol.format.IGC,
                //ol.format.KML,
                //ol.format.TopoJSON
            ]
        });
        this.dragAndDropInteraction.on('addfeatures', this.onDragDropAddFeatures);
        this.map.addInteraction(this.dragAndDropInteraction);
    }

    private addRemoveAllHandler = (): void => {
        let self = this;
        $("#btnGpxClear").on("click", function () {
            let fileNames = self.getStoredFileNames();
            fileNames.forEach(function (val) {
                self.appStorage.clearFromSession(val.filename);
            });
            self.appStorage.clearFromSession("GpxFiles");
            self.layerSource.clear();
            self.buildGpxUi();
        });
    }

    private loadFromStorage = (): void => {
        let self = this;
        let geoJsonformat = new GeoJSON();
        let fileNamesAndColours = this.getStoredFileNames();

        fileNamesAndColours.forEach(function (val) {
            let geoJsonStr = <IGpxFile>self.appStorage.getSessionValueFromJson(val.filename);
            if (geoJsonStr) {
                let features = geoJsonformat.readFeatures(geoJsonStr.geoJson);

                features.forEach(function (feature) {
                    feature.setStyle(self.getFeatureStyle(val.colour));
                    feature.set("filename", val.filename, true);
                });

                self.layerSource.addFeatures(features);
            }
        });
        this.buildGpxUi();
    }

    private onDragDropAddFeatures = (event): void => {
        if (!$("#slide-panel-tools").is(':visible')) {
            $("#slide-panel-btn-tools").click();
        }
        this.loadGpx(event.file, event.features);
    }

    private getFeaturesFromGpxFile = (file, callback): void => {
        let gpxFeatures = null,
            gpxFormat = new GPX(),
            reader = new FileReader();

        reader.readAsText(file, "UTF-8");
        reader.onload = function (fileReader) {
            gpxFeatures = gpxFormat.readFeatures(fileReader.target.result, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857'
            });

            callback.call(file, gpxFeatures);
        }
    }

    private loadGpx = (file, features): void => {
        this.layerSource.addFeatures(features);

        let filename = file.name.replace(/\.gpx/gi, "").replace(/\s/g, "_").replace(/[^\w]/g, "");
        let extent = olExtent.createEmpty();

        for (let i = 0; i < features.length; i++) {
            olExtent.extend(extent, features[i].getGeometry().getExtent());
        }

        this.storeFeaturesAsGeoJson(filename, { features: features, extent: extent });
        this.zoomToGpx(filename);
    }

    private zoomToGpx = (fileName:string): void => {
        let gpxFile: IGpxFile = <IGpxFile>this.appStorage.getSessionValueFromJson(fileName);

        if (gpxFile) {
            this.map.getView().fit(gpxFile.extent, { nearest: false });
        }
    }

    private storeFeaturesAsGeoJson = (fileName, gpx): void => {
        let writer = new GeoJSON();

        //add the file name to each feature
        if (gpx.features) {
            gpx.features.forEach(function (feature) {
                let p = feature.getProperties();
                p.filename = fileName;
                feature.setProperties(p, true);
            });
        }

        let geojsonStr = writer.writeFeatures(gpx.features);
        let storedFiles = this.appStorage.getSessionValueFromJson("GpxFiles");
        if (!storedFiles) {
            storedFiles = {};
        }
        storedFiles[fileName] = this.defaultColour;
        //add the file name
        this.appStorage.setSessionValue("GpxFiles", JSON.stringify(storedFiles));
        //add the geojson & extent
        this.appStorage.setSessionValue(fileName, JSON.stringify({ geoJson: geojsonStr, extent: gpx.extent }));

        this.buildGpxUi();
    }


    private buildGpxUi = (): void => {
        let self = this;
        let container = $("#gpxFiles");
        //clear any existing ui
        container.empty();

        let fileNames: Array<IGpxFiles> = this.getStoredFileNames();
        fileNames.forEach(function (gpx) {

            let dataFileName = "data-file-name='" + gpx.filename + "'";

            container.append("" +
                "<div class='d-flex justify-content-between mb-2 align-items-end'>" +
                "  <button style='width:150px;overflow-wrap: break-word;' id='" + gpx.filename + "' type='button' class='btn btn-sm btn-secondary me-1' data-gpx-action='zoom' " + dataFileName + " title='Zoom to GPX'>" + gpx.filename + "&nbsp;" +
                "    <i class='fas fa-search-plus'></i>" +
                "  </button>" +
                "  <input type='color' id='gpxColour_" + gpx.filename + "' name='gpxColour" + gpx.filename + "' value='" + gpx.colour + "' " + dataFileName + "> " +
                "  <i class='fa-solid fa-circle-info ms-1 me-1' data-bs-container='#gpxFiles' data-bs-toggle='popover' data-bs-content='Once you have picked a colour hit enter.'></i>" +
                "  <button class='btn btn-sm btn-secondary ml-2' data-gpx-action='delete' " + dataFileName + " title='Remove GPX'><i class='fas fa-trash'></i></button>" +
                "</div>"
            );

            $("#gpxColour_" + gpx.filename).on("change", function () {
                let colour = $(this).val();
                let filename = $(this).data("fileName")

                //console.log(colour + "," + filename);                

                self.updateGpxColour(filename, colour);
            });
        });
               
        const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'))
        const popoverList = popoverTriggerList.map(function (popoverTriggerEl) {
            return new Popover(popoverTriggerEl)
        })

        $('[data-gpx-action]').on("click", function () {
            let action = $(this).data("gpxAction");
            if (action === "delete") {
                self.removeGpxFile($(this).data("fileName"));
            }
            else if (action === "zoom") {
                self.zoomToGpx($(this).data("fileName"));
            }
        });
    }

    private updateGpxColour = (filename, colour): void => {
        let self = this;
        let storedFiles = this.appStorage.getSessionValueFromJson("GpxFiles");
        storedFiles[filename] = colour;

        this.layerSource.getFeatures().forEach(function (feature) {
            if (feature.get("filename") === filename) {
                feature.setStyle(self.getFeatureStyle(colour));
            }
        });

        //add the file name
        this.appStorage.setSessionValue("GpxFiles", JSON.stringify(storedFiles));
    }

    private removeGpxFile = (fileName): void => {
        //remove geojson from storage
        this.appStorage.clearFromSession(fileName);

        let storedFiles = this.appStorage.getSessionValueFromJson("GpxFiles");
        delete storedFiles[fileName];
        this.appStorage.setSessionValue("GpxFiles", JSON.stringify(storedFiles));

        //now remove all features from that gpx file from the map
        let allFeatures = this.layerSource.getFeatures();
        allFeatures.forEach(function (feature) {
            if (feature.getProperties().fileName === fileName) {
                this.layerSource.removeFeature(feature);
            }
        });

        //rebuild the ui
        this.buildGpxUi();
    }

    private getStoredFileNames = (): Array<IGpxFiles> => {
        let storedFiles = this.appStorage.getSessionValueFromJson("GpxFiles");
        if (!storedFiles) {
            return new Array<IGpxFiles>();
        }

        let fileNames = new Array<IGpxFiles>();
        for (let file in storedFiles) {
            if (storedFiles.hasOwnProperty(file)) {
                fileNames.push({
                    filename: file,
                    colour: storedFiles[file]
                });
            }
        }
        return fileNames;
    }

}